import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CodeBlock,
  GuideBulletList,
  GuideCodeTable,
  GuideHeading,
  GuideModal,
  GuideNote,
  GuideSection,
  GuideSteps,
  GuideText,
} from 'modules/settings/components/DataExport/GuideContent';
import { GuideBody, GuideTab, GuideTabs } from 'modules/settings/components/DataExport/styled';
import { TOKEN_PLACEHOLDER } from 'modules/settings/components/DataExport/DownloadApiGuide';

const EVENTS = ['study.updated', 'study.deleted', 'study.expired'] as const;
const HEADERS = ['contentType', 'userAgent', 'event', 'delivery', 'signature'] as const;
const ENDPOINT_RULES = ['verify', 'respond', 'deduplicate'] as const;
const FEED_RULES = ['cursor', 'retention', 'guarantee'] as const;

const REGISTRATION_RULES = ['url', 'secret'] as const;

export const PYTHON_VERIFY_EXAMPLE = `import hashlib, hmac, time

def verify(raw_body: bytes, signature_header: str, secret: str, tolerance=300) -> bool:
    parts = dict(item.split("=", 1) for item in signature_header.split(","))
    timestamp, received = parts.get("t", ""), parts.get("v1", "")
    if not timestamp.isdigit() or abs(time.time() - int(timestamp)) > tolerance:
        return False
    expected = hmac.new(secret.encode(), f"{timestamp}.".encode() + raw_body, hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, received)
`;

const EXAMPLE_BODY = JSON.stringify(
  {
    event: 'study.updated',
    delivery_id: '6f1c0a52-3f7e-4bb4-9a0e-2a1f5c1d9e7b',
    occurred_at: '2026-10-05T13:19:39Z',
    study: { public_id: 'f949f880-f671-4469-8195-426e93568e43' },
    export_sequence: 8,
    sequence: 42,
  },
  null,
  2,
);

const PING_BODY = JSON.stringify(
  { event: 'ping', delivery_id: '0b7f6c1e-2d4a-4c35-9f61-8a2e5d3c1b90', occurred_at: '2026-10-05T13:20:02Z' },
  null,
  2,
);

/** Catching up from the event feed; `url` is this organisation's download API. */
export const eventFeedCurlExample = (url: string): string =>
  `curl -H "Authorization: Bearer ${TOKEN_PLACEHOLDER}" "${url}/v1/events?after=<last sequence you processed>"`;

/**
 * `maxSubscriptions` comes from the webhooks response (the limit line is left out until it is known), and
 * `downloadApiUrl` from the tokens response (the event feed example waits for it).
 */
const TABS = ['overview', 'payload', 'verify', 'feed'] as const;
type Tab = typeof TABS[number];

const WebhooksGuide: React.FC<{ maxSubscriptions?: number; downloadApiUrl?: string }> = ({ maxSubscriptions, downloadApiUrl }) => {
  const { t } = useTranslation();
  const [tab, setTab] = React.useState<Tab>('overview');

  const overview = (
    <>
      <GuideSection>
        <GuideHeading>{t('dataExport.webhooks.guide.registerTitle')}</GuideHeading>
        <GuideBulletList
          items={[
            ...REGISTRATION_RULES.map(rule => t(`dataExport.webhooks.guide.register.${rule}`)),
            ...(maxSubscriptions !== undefined ? [t('dataExport.webhooks.guide.register.limit', { count: maxSubscriptions })] : []),
          ]}
        />
      </GuideSection>
      <GuideSection>
        <GuideHeading>{t('dataExport.webhooks.guide.deliveriesTitle')}</GuideHeading>
        <GuideText>{t('dataExport.webhooks.guide.intro')}</GuideText>
        <GuideCodeTable
          rows={EVENTS.map(event => ({ code: event, text: t(`dataExport.webhooks.guide.events.${event.replace('study.', '')}`) }))}
        />
      </GuideSection>
      <GuideSection>
        <GuideHeading>{t('dataExport.webhooks.guide.endpointTitle')}</GuideHeading>
        <GuideSteps>
          {ENDPOINT_RULES.map(rule => (
            <li key={rule}>
              <GuideText>{t(`dataExport.webhooks.guide.endpoint.${rule}`)}</GuideText>
            </li>
          ))}
        </GuideSteps>
      </GuideSection>
      <GuideNote>
        <GuideText>{t('dataExport.webhooks.guide.paused')}</GuideText>
      </GuideNote>
    </>
  );

  const payload = (
    <>
      <GuideSection>
        <GuideHeading>{t('dataExport.webhooks.guide.bodyTitle')}</GuideHeading>
        <CodeBlock code={EXAMPLE_BODY} language='json' data-testid='data-export-code-example' />
        <GuideText>{t('dataExport.webhooks.guide.sequence')}</GuideText>
      </GuideSection>
      <GuideSection>
        <GuideHeading>{t('dataExport.webhooks.guide.pingTitle')}</GuideHeading>
        <GuideText>{t('dataExport.webhooks.guide.ping')}</GuideText>
        <CodeBlock code={PING_BODY} language='json' data-testid='data-export-ping-example' />
      </GuideSection>
      <GuideSection>
        <GuideHeading>{t('dataExport.webhooks.guide.headersTitle')}</GuideHeading>
        <GuideBulletList items={HEADERS.map(header => t(`dataExport.webhooks.guide.headers.${header}`))} />
      </GuideSection>
    </>
  );

  const verify = (
    <GuideSection>
      <GuideHeading>{t('dataExport.webhooks.guide.verifyTitle')}</GuideHeading>
      <CodeBlock code={PYTHON_VERIFY_EXAMPLE} language='python' data-testid='data-export-python-example' />
    </GuideSection>
  );

  const feed = (
    <GuideSection>
      <GuideHeading>{t('dataExport.webhooks.guide.feedTitle')}</GuideHeading>
      <GuideText>{t('dataExport.webhooks.guide.feed')}</GuideText>
      <GuideBulletList items={FEED_RULES.map(rule => t(`dataExport.webhooks.guide.feedRules.${rule}`))} />
      {downloadApiUrl && (
        <CodeBlock code={eventFeedCurlExample(downloadApiUrl)} language='bash' wrap data-testid='data-export-feed-example' />
      )}
    </GuideSection>
  );

  const content: Record<Tab, JSX.Element> = { overview, payload, verify, feed };

  return (
    <GuideModal id='data-export-webhooks-guide' title={t('dataExport.webhooks.guide.title')}>
      <GuideTabs role='tablist'>
        {TABS.map(name => (
          <GuideTab key={name} type='button' role='tab' aria-selected={tab === name} onClick={() => setTab(name)}>
            {t(`dataExport.webhooks.guide.tabs.${name}`)}
          </GuideTab>
        ))}
      </GuideTabs>
      <GuideBody role='tabpanel' data-testid='data-export-webhooks-guide'>
        {content[tab]}
      </GuideBody>
    </GuideModal>
  );
};

export default React.memo(WebhooksGuide);
